import { LEGAL_COUNTRIES } from "./legal";

/**
 * Workshop settings. Every setting needs `setting.<key>.label` and
 * `setting.<key>.description` in every locale file; the manual lists them all
 * with their defaults (tools/manual checks this).
 */
export type SettingType = "money" | "percent" | "currency" | "text" | "longtext" | "switch" | "select" | "days" | "count";

export interface SettingDefinition {
  key: string;
  type: SettingType;
  /** Default value; null means the setup wizard must ask for it. */
  default: number | string | boolean | null;
  /** Choices of a "select" setting; each needs `setting.<key>.option.<value>`. */
  options?: readonly string[];
}

export const SETTINGS: readonly SettingDefinition[] = [
  { key: "hourlyRateNet", type: "money", default: null },
  { key: "defaultVatRate", type: "percent", default: 27 },
  { key: "currency", type: "currency", default: "HUF" },
  { key: "workOrderPrefix", type: "text", default: "ML" },
  // Default margin for new articles (stock keeping itself is the "stock" feature)
  { key: "defaultMarkupPercent", type: "percent", default: null },
  // Printed on the work order sheet
  { key: "workshopAddress", type: "text", default: null },
  { key: "workshopPhone", type: "text", default: null },
  // Customers' replies to the program's e-mails go here
  { key: "workshopEmail", type: "text", default: null },
  { key: "workshopTaxNumber", type: "text", default: null },
  { key: "workshopRegNumber", type: "text", default: null },
  { key: "printNote", type: "longtext", default: null },
  // Legal texts of the official work order follow this country's rules
  { key: "legalCountry", type: "select", default: "HU", options: LEGAL_COUNTRIES },
  // Own warranty terms instead of the statutory text (e.g. a longer warranty)
  { key: "warrantyText", type: "longtext", default: null },
  // Who sends the SMS (the "sms" feature): Garagevo for a fee per SMS, or the workshop's own Android phone for free
  { key: "smsProvider", type: "select", default: "garagevo", options: ["garagevo", "ownPhone"] },
  // Reminders (the "reminders" feature): channels and how many days before the due date
  // (keep the defaults equal to REMINDER_DEFAULT_DAYS and public.due_reminders())
  { key: "reminderEmail", type: "switch", default: true },
  { key: "reminderSms", type: "switch", default: false },
  { key: "reminderInspectionDays", type: "days", default: 30 },
  { key: "reminderServiceDays", type: "days", default: 14 },
  // Two-step sign in for managers too (owner only; always required for the owner, except in test workshops)
  { key: "mfaManagers", type: "switch", default: false },
  // Intake with photos (the "intakePhotos" feature, its own panel; keep the defaults equal to intakeSettings in intake.ts)
  { key: "intakePhotoMode", type: "select", default: "offer", options: ["offer", "required"] },
  { key: "intakePhotoMin", type: "count", default: 5 },
  { key: "intakePhotoSend", type: "select", default: "ifEmail", options: ["ifEmail", "never"] },
  { key: "intakePhotoRetention", type: "select", default: "warrantyPlus1y", options: ["warrantyPlus1y", "5y"] },
];
